import { Box, CircularProgress } from "@mui/material"
import React, { FormEvent, useCallback, useContext, useEffect } from "react"
import {
  BOTTOM_NAVIGATION_HEIGHT,
  CustomBottomNavigation,
} from "../../../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { SuccessContext } from "../../../../../../../../components/layout/success-snackbar"
import { TabEnum } from "../../../../../../../../components/tabs/tabs"
import { BSInputContext } from "../../../../../../../../core/context/beem-shot/BSInput/BSInputContext"
import { CalculStatusEnum } from "../../../../../../../../core/enum/calculStatusEnum"
import { BSParkingFormDialog } from "../../../building/BSCreateBuildingPages/BSBuildingParkingPage/components/BSParkingFormDialog"
import { BSParkingContext } from "../../../building/BSCreateBuildingPages/BSBuildingParkingPage/context/BSParkingContext"
import { BSPatioContext } from "../../../building/BSCreateBuildingPages/BSPatioPage/context/BSPatioContext"
import { BSBuildingZoneContainer } from "../../../building/BSCreateBuildingPages/BSZoneAndLevelPage/components/BSBuildingZoneNoBim/BSBuildingZoneContainer"
import { BSLevelContext } from "../../../building/BSCreateBuildingPages/BSZoneAndLevelPage/context/BSLevelContext"
import {
  BSLevelFormContext,
  BSLevelFormContextProvider,
} from "../../../building/BSCreateBuildingPages/BSZoneAndLevelPage/context/BSLevelFormContext"
import {
  BSUsageContext,
  BSUsageContextProvider,
} from "../../../building/BSCreateBuildingPages/BSZoneAndLevelPage/context/BSUsageContext"
import { BSBuildingContext } from "../../../building/components/context/BSBuildingContext"
import { BSVariantControlPanelContext } from "../../../components/BSVariantControlPanelContext"
import { ControlPanelTabHeader } from "../../../components/ControlPanelTabHeader"

interface IProps {
  setSelectedTab?: React.Dispatch<React.SetStateAction<number>>
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>
}

export function BSBuildingZoneAndLevelTab({ setSelectedTab, setIsUpdating }: Readonly<IProps>): React.JSX.Element {
  return (
    <BSUsageContextProvider>
      <BSLevelFormContextProvider>
        <BSBuildingZoneTabCore setSelectedTab={setSelectedTab} setIsUpdating={setIsUpdating} />
      </BSLevelFormContextProvider>
    </BSUsageContextProvider>
  )
}

export function BSBuildingZoneTabCore({
  setSelectedTab,
  setIsUpdating: setIsUpdatingForDialog,
}: Readonly<IProps>): React.JSX.Element {
  const openSuccessSnackbar = useContext(SuccessContext)
  const { controlPanelRefWidth } = useContext(BSVariantControlPanelContext)
  const { selectedBSBuilding, updateCalculStatusState } = useContext(BSBuildingContext)
  const { bsInput } = useContext(BSInputContext)
  const { isLevelRefreshing, setIsLevelUpdated, hasToControlPatio, setHasToControlPatio } = useContext(BSLevelContext)
  const { validateLevelForm, submitBSBuildingLevelForm, isLevelListSubmitting, setIsLevelListSubmitting } =
    useContext(BSLevelFormContext)

  const { bsPatioList } = useContext(BSPatioContext)
  const { isParkingFormOpen, setIsParkingFormOpen, isParkingFormForSuperstructure } = useContext(BSParkingContext)
  const { handleSubmit: handleSubmitUsage, isUsageUpdating, isSubmitting: isSubmittingUsage } = useContext(BSUsageContext)

  useEffect(() => {
    setIsUpdatingForDialog(isLevelRefreshing || isUsageUpdating)
  }, [isLevelRefreshing, isUsageUpdating, setIsUpdatingForDialog])

  const handleSubmitLevel: (event: FormEvent<HTMLFormElement>) => Promise<any> = useCallback(
    (event) => {
      event.preventDefault()

      if (!selectedBSBuilding?.bim && selectedBSBuilding?.id && validateLevelForm()) {
        setIsLevelListSubmitting(true)
        return submitBSBuildingLevelForm(selectedBSBuilding?.id)
          .then(() => {
            updateCalculStatusState(CalculStatusEnum.WAITING_FOR_CONFIGURATION)
            openSuccessSnackbar("Les informations ont bien été enregistré")
          })
          .finally(() => {
            setIsLevelUpdated(false)
            if (setSelectedTab && hasToControlPatio && bsPatioList && bsPatioList?.length > 0) {
              setSelectedTab(TabEnum.BUILDING_PATIO)
            } else {
              setHasToControlPatio(false)
            }
          })
      } else {
        return Promise.resolve()
      }
    },
    [
      selectedBSBuilding?.bim,
      selectedBSBuilding?.id,
      validateLevelForm,
      setIsLevelListSubmitting,
      submitBSBuildingLevelForm,
      openSuccessSnackbar,
      setIsLevelUpdated,
      setSelectedTab,
      hasToControlPatio,
      bsPatioList,
      updateCalculStatusState,
      setHasToControlPatio,
    ]
  )

  const handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<any> = useCallback(
    (event) => {
      event.preventDefault()
      return handleSubmitUsage().then((response) => {
        if (response !== undefined) {
          handleSubmitLevel(event)
        }
      })
    },
    [handleSubmitUsage, handleSubmitLevel]
  )

  return (
    <>
      <Box
        height={isLevelRefreshing || isUsageUpdating ? `calc(100% - ${BOTTOM_NAVIGATION_HEIGHT} -  2vh)` : "100%"}
        component="form"
        id="update-usage-and-level"
        onSubmit={handleSubmit}
        px={5}
        pb={2}
        sx={{ overflow: "auto" }}>
        {bsInput && selectedBSBuilding ? (
          <>
            <ControlPanelTabHeader title="Niveaux et zones" />

            <BSBuildingZoneContainer />
          </>
        ) : (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {(isLevelRefreshing || isUsageUpdating) && (
          <CustomBottomNavigation
            actionLabel="Enregistrer"
            formId="update-usage-and-level"
            isSubmitting={isLevelListSubmitting ?? isSubmittingUsage}
            additionalLeftPadding={controlPanelRefWidth}
          />
        )}
      </Box>

      {/* as its a form dialog, it has to be out of the main form */}
      <BSParkingFormDialog
        isOpen={isParkingFormOpen}
        setIsOpen={setIsParkingFormOpen}
        isSuperstructure={isParkingFormForSuperstructure}
      />
    </>
  )
}
